import { parseDateSafe } from '../utils/utils';
import { INTERIM_GLOBAL_WORK_HOURS } from './dhtmlxProjectCalendars';
import {
  applyCalendarDayKeepingTime,
  toCalendarDayString,
} from './ganttDateFormat';
import { computeDurationFromStartAndFinish } from './addTaskUtils';

/** Default end-of-work when calendar hours are unknown (MSP-style day end). */
export const DEFAULT_WORKDAY_END_HM = '17:00';

export type WorkHoursInterval = string;

/**
 * Last interval end from hour ranges like ["08:00-12:00","13:00-17:00"] → "17:00".
 */
export function lastWorkIntervalEndHm(
  hours: readonly WorkHoursInterval[] = INTERIM_GLOBAL_WORK_HOURS,
): string {
  let last = DEFAULT_WORKDAY_END_HM;
  for (const raw of hours) {
    const m = String(raw).match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
    if (m) last = m[2];
  }
  return last;
}

function parseHm(hm: string): { hours: number; minutes: number } {
  const [h, m] = hm.split(':').map((x) => Number(x));
  return {
    hours: Number.isFinite(h) ? h : 17,
    minutes: Number.isFinite(m) ? m : 0,
  };
}

function toLocalDate(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value == null || String(value).trim() === '') return null;
  const parsed = parseDateSafe(String(value));
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function isMidnightLocal(date: Date): boolean {
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

/**
 * Inclusive MSP finish day → DHTMLX exclusive end_date at end of work that day.
 * Milestone / duration 0: end_date equals start_date when start is provided.
 */
export function inclusiveFinishToExclusiveEndDate(
  finish: string | Date | null | undefined,
  options?: {
    start?: string | Date | null;
    duration?: number | null;
    workHoursEndHm?: string;
  },
): Date | null {
  const start = toLocalDate(options?.start ?? null);
  const duration =
    options?.duration != null && Number.isFinite(Number(options.duration))
      ? Number(options.duration)
      : null;

  if (duration === 0 && start) {
    return new Date(start.getTime());
  }

  const finishDate = toLocalDate(finish);
  if (!finishDate) return start ? new Date(start.getTime()) : null;

  const hm = parseHm(options?.workHoursEndHm ?? lastWorkIntervalEndHm());
  return new Date(
    finishDate.getFullYear(),
    finishDate.getMonth(),
    finishDate.getDate(),
    hm.hours,
    hm.minutes,
    0,
    0,
  );
}

/**
 * DHTMLX exclusive end_date → inclusive finish calendar day (YYYY-MM-DD).
 * Midnight exclusive → previous calendar day; otherwise calendar day of end_date.
 */
export function exclusiveEndDateToInclusiveFinishDay(
  endDate: string | Date | null | undefined,
): string | null {
  const end = toLocalDate(endDate);
  if (!end) return null;

  if (isMidnightLocal(end)) {
    const prev = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
    return toCalendarDayString(prev);
  }
  return toCalendarDayString(end);
}

/**
 * Grid End editor: the typed calendar day is inclusive MSP Finish
 * (never treat editor midnight as exclusive next-day).
 */
export function endEditorValueToInclusiveFinishDay(
  endEditorValue: string | Date | null | undefined,
): string | null {
  return toCalendarDayString(endEditorValue);
}

/**
 * Inclusive finish wall-clock string for store, keeping existing finish time-of-day.
 */
export function exclusiveEndToInclusiveFinishWallClock(
  endDate: string | Date | null | undefined,
  existingFinish: string | null | undefined,
  options?: { fromEndEditor?: boolean },
): string | null {
  const day = options?.fromEndEditor
    ? endEditorValueToInclusiveFinishDay(endDate)
    : exclusiveEndDateToInclusiveFinishDay(endDate);
  if (!day) return null;
  return applyCalendarDayKeepingTime(day, existingFinish ?? '17:00:00');
}

export type DhtmlxScheduleFields = {
  start_date: Date | null;
  end_date: Date | null;
  duration: number | null;
};

/**
 * Build DHTMLX start/end/duration from inclusive store snapshot fields.
 */
export function buildDhtmlxScheduleFields(input: {
  start?: string | Date | null;
  finish?: string | Date | null;
  duration?: number | null;
  milestone?: boolean | null;
  workHoursEndHm?: string;
}): DhtmlxScheduleFields {
  const start_date = toLocalDate(input.start ?? null);
  const durationRaw =
    input.duration != null && Number.isFinite(Number(input.duration))
      ? Number(input.duration)
      : null;
  const isMilestone = !!input.milestone || durationRaw === 0;

  if (isMilestone) {
    const instant = start_date ?? toLocalDate(input.finish ?? null);
    return {
      start_date: instant,
      end_date: instant ? new Date(instant.getTime()) : null,
      duration: 0,
    };
  }

  let duration = durationRaw;
  if (
    (duration == null || !Number.isFinite(duration)) &&
    input.start &&
    input.finish
  ) {
    duration = computeDurationFromStartAndFinish(input.start, input.finish);
  }

  const end_date = inclusiveFinishToExclusiveEndDate(input.finish, {
    start: input.start,
    duration,
    workHoursEndHm: input.workHoursEndHm,
  });

  return {
    start_date,
    end_date,
    duration: duration != null && Number.isFinite(duration) ? duration : null,
  };
}
